import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { FaPlus, FaTrash, FaPencilAlt, FaChartPie, FaRegLightbulb, FaChartBar, FaList } from 'react-icons/fa';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { toast } from 'react-toastify';
import DeleteModal from '../components/DeleteModal';
import { CURRENCY_SYMBOLS } from '../utils/currencySymbols';
import BudgetTemplateModal from '../components/BudgetTemplateModal';
import MonthSelector from '../components/MonthSelector';
import { motion } from 'framer-motion';
import BarChartStructure from '../components/BarChartStructure';
import { Tab } from '@headlessui/react';
import { CURRENCY_SYMBOLS_TEXT } from '../utils/currencySymbols';

const Budgets = () => {
  const [templates, setTemplates] = useState([]);
  const [monthlyBudgets, setMonthlyBudgets] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [categories, setCategories] = useState([]);
  const [userCurrencies, setUserCurrencies] = useState(['GBP']); // Default currency
  const { currentUser } = useAuth();
  const [monthlyExpenses, setMonthlyExpenses] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState('spending'); // 'spending', 'category', 'budget'
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc', 'desc'
  const [expandedTemplates, setExpandedTemplates] = useState({});
  const [selectedCurrency, setSelectedCurrency] = useState(userCurrencies[0]);

  // Fetch user data, templates, and budgets when component mounts
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;

      try {
        // Fetch settings
        const settingsDoc = await getDoc(doc(db, 'userSettings', currentUser.uid));
        if (settingsDoc.exists()) {
          const { currencies } = settingsDoc.data();
          setUserCurrencies(currencies);
        }

        // Fetch categories
        const categoriesDoc = await getDoc(doc(db, 'categories', currentUser.uid));
        if (categoriesDoc.exists()) {
          setCategories(categoriesDoc.data().items || []);
        }

        // Fetch budget templates
        const templatesDoc = await getDoc(doc(db, 'budgetTemplates', currentUser.uid));
        if (templatesDoc.exists()) {
          setTemplates(templatesDoc.data().items || []);
        }

        // Fetch monthly budgets
        const budgetsDoc = await getDoc(doc(db, 'monthlyBudgets', currentUser.uid));
        if (budgetsDoc.exists()) {
          setMonthlyBudgets(budgetsDoc.data().items || {});
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load budget data');
      }
    };

    fetchData();
  }, [currentUser]);

  // Fetch expenses for the selected month
  useEffect(() => {
    const fetchMonthlyExpenses = async () => {
      if (!currentUser || !selectedMonth) return;

      try {
        setIsLoading(true);
        
        // Get all possible dates for the month
        const startDate = startOfMonth(new Date(selectedMonth));
        const endDate = endOfMonth(new Date(selectedMonth));

        // Get the expenses document directly
        const expensesDoc = doc(db, 'expenses', currentUser.uid);
        const expensesSnapshot = await getDoc(expensesDoc);
        
        if (expensesSnapshot.exists()) {
          const expenses = {};
          const expensesData = expensesSnapshot.data();

          // Check if there's an items field
          if (expensesData.items) {
            Object.entries(expensesData.items).forEach(([date, dateExpenses]) => {
              if (date >= format(startDate, 'yyyy-MM-dd') && 
                  date <= format(endDate, 'yyyy-MM-dd')) {
                
                // Process each expense in the date
                Object.entries(dateExpenses).forEach(([key, expense]) => {
                  if (expense && expense.category) {
                    if (!expenses[expense.category]) {
                      expenses[expense.category] = {
                        total: 0,
                        currency: expense.currency,
                        expenses: []
                      };
                    }

                    const amount = Number(expense.amount);
                    if (!isNaN(amount)) {
                      expenses[expense.category].total += amount;
                      expenses[expense.category].expenses.push({
                        ...expense,
                        id: `${date}-${key}`,
                        amount: amount
                      });
                    }
                  }
                });
              }
            });
          }

          setMonthlyExpenses(expenses);
        }
      } catch (error) {
        console.error('Error fetching expenses:', error);
        toast.error('Failed to load expenses');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMonthlyExpenses();
  }, [currentUser, selectedMonth]);

  const handleCreateTemplate = async (templateData) => {
    try {
      const newTemplate = {
        id: Date.now().toString(),
        title: templateData.title,
        description: templateData.description || '', // Ensure description is never undefined
        categories: templateData.categories.map(cat => ({
          category: cat.category,
          amount: cat.amount,
          currency: cat.currency
        })),
        createdAt: new Date().toISOString()
      };

      // Check if document exists first
      const templateDoc = doc(db, 'budgetTemplates', currentUser.uid);
      const templateSnapshot = await getDoc(templateDoc);

      if (templateSnapshot.exists()) {
        // Update existing document
        await updateDoc(templateDoc, {
          items: [...templates, newTemplate]
        });
      } else {
        // Create new document
        await setDoc(templateDoc, {
          items: [newTemplate]
        });
      }

      setTemplates(prev => [...prev, newTemplate]);
      setIsTemplateModalOpen(false);
      toast.success('Template created successfully');
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Failed to create template');
    }
  };

  const handleUpdateTemplate = async (templateData) => {
    try {
      const updatedTemplates = templates.map(template =>
        template.id === templateData.id ? templateData : template
      );

      await updateDoc(doc(db, 'budgetTemplates', currentUser.uid), {
        items: updatedTemplates
      });

      setTemplates(updatedTemplates);
      setIsTemplateModalOpen(false);
      setEditingTemplate(null);
      toast.success('Template updated successfully');
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error('Failed to update template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      const updatedTemplates = templates.filter(template => template.id !== templateId);
      
      await updateDoc(doc(db, 'budgetTemplates', currentUser.uid), {
        items: updatedTemplates
      });

      setTemplates(updatedTemplates);
      setDeleteModalOpen(false);
      setItemToDelete(null);
      toast.success('Template deleted successfully');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleApplyTemplate = async (templateId, month) => {
    try {
      const template = templates.find(t => t.id === templateId);
      if (!template) throw new Error('Template not found');

      const monthlyBudgetDoc = doc(db, 'monthlyBudgets', currentUser.uid);
      const monthlyBudgetSnapshot = await getDoc(monthlyBudgetDoc);

      const updatedBudgets = {
        ...monthlyBudgets,
        [month]: {
          templateId,
          categories: template.categories,
          appliedAt: new Date().toISOString()
        }
      };

      if (monthlyBudgetSnapshot.exists()) {
        // Update existing document
        await updateDoc(monthlyBudgetDoc, {
          items: updatedBudgets
        });
      } else {
        // Create new document
        await setDoc(monthlyBudgetDoc, {
          items: updatedBudgets
        });
      }

      setMonthlyBudgets(updatedBudgets);
      toast.success('Template applied successfully');
    } catch (error) {
      console.error('Error applying template:', error);
      toast.error('Failed to apply template');
    }
  };

  // Calculate budget progress with better number handling
  const getBudgetProgress = (category, budgetAmount, currency) => {
    const categoryExpenses = monthlyExpenses[category];
    
    // Convert budgetAmount to number if it's a string
    const budget = typeof budgetAmount === 'string' 
      ? parseFloat(budgetAmount) 
      : Number(budgetAmount) || 0;

    if (!categoryExpenses) {
      return {
        spent: 0,
        progress: 0,
        remaining: budget,
        isOverBudget: false
      };
    }

    // Ensure spent is a number
    const spent = Number(categoryExpenses.total) || 0;
    const progress = budget > 0 ? (spent / budget) * 100 : 0;
    const remaining = budget - spent;
    
    return {
      spent,
      progress: progress,
      remaining: remaining,
      isOverBudget: progress > 100
    };
  };

  // Add this helper function at the top of your component
  const calculateTemplateTotal = (categories) => {
    return categories.reduce((total, cat) => {
      const amount = Number(cat.amount) || 0;
      return total + amount;
    }, 0);
  };

  // Update the calculateMonthlyTotals function
  const calculateMonthlyTotals = () => {
    const currencyTotals = {};
    
    // Initialize totals for each currency
    userCurrencies.forEach(currency => {
      currencyTotals[currency] = {
        budget: 0,
        spent: 0
      };
    });

    // Calculate budget totals by currency
    monthlyBudgets[selectedMonth]?.categories?.forEach(cat => {
      const amount = Number(cat.amount) || 0;
      const currency = cat.currency || userCurrencies[0];
      currencyTotals[currency].budget += amount;
    });

    // Calculate spent totals by currency
    Object.values(monthlyExpenses).forEach(category => {
      const amount = Number(category.total) || 0;
      const currency = category.currency || userCurrencies[0];
      currencyTotals[currency].spent += amount;
    });

    return {
      currencyTotals,
      // Keep these for backward compatibility
      totalBudget: currencyTotals[selectedCurrency]?.budget || 0,
      totalSpent: currencyTotals[selectedCurrency]?.spent || 0
    };
  };

  // Update the prepareBudgetChartData function to filter by currency
  const prepareBudgetChartData = (currency) => {
    return categories.map(category => {
      const budgetCategory = monthlyBudgets[selectedMonth]?.categories?.find(
        cat => cat.category === category && cat.currency === currency
      );
      const expenses = monthlyExpenses[category];
      const expenseAmount = expenses?.currency === currency ? expenses.total : 0;
      
      return {
        name: category,
        Budget: Number(Number(budgetCategory?.amount || 0).toFixed(2)),
        Spent: Number(Number(expenseAmount || 0).toFixed(2))
      };
    }).sort((a, b) => b.Spent - a.Spent);
  };

  // Add this helper function to compare template categories
  const areTemplatesEqual = (template1, template2) => {
    if (!template1 || !template2) return false;
    
    if (template1.length !== template2.length) return false;
    
    return template1.every(cat1 => {
      const cat2 = template2.find(cat2 => cat2.category === cat1.category);
      return cat2 && 
             cat2.amount === cat1.amount && 
             cat2.currency === cat1.currency;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto py-6 px-4">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <h1 className="text-2xl md:text-4xl min-h-12 font-bold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">
            Smart Budget Management
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Create templates, track spending, and achieve your financial goals
          </p>
        </motion.div>

        {/* Quick Actions */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
        >

          <div 
            onClick={() => {
              setEditingTemplate(null);
              setIsTemplateModalOpen(true);
            }}
            className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all cursor-pointer group"
          >
            <div className="flex items-center justify-between">
              <div className="text-white">
                <h3 className="text-lg font-semibold mb-1">Create Template</h3>
                <p className="text-sm opacity-90">Design a new budget template</p>
              </div>
              <div className="bg-white/20 p-3 rounded-full group-hover:scale-110 transition-transform">
                <FaPlus className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-900 rounded-xl p-6 shadow-xl hover:shadow-2xl transition-shadow backdrop-blur-lg bg-opacity-50 dark:bg-opacity-50">
            <div className="flex flex-col">
              <h3 className="text-lg font-semibold mb-1 text-gray-900 dark:text-gray-100">
                Select Month
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Choose period to view
              </p>
              <div className="flex items-center justify-center">
                <MonthSelector 
                  selectedMonth={selectedMonth} 
                  onChange={setSelectedMonth}
                />
              </div>
            </div>
          </div>


          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-xl hover:shadow-2xl transition-shadow backdrop-blur-lg bg-opacity-50 dark:bg-opacity-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold mb-1 text-gray-900 dark:text-gray-100">
                  Quick Stats
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {templates.length} templates created
                </p>
              </div>
              <div className="p-3 rounded-full bg-indigo-100 dark:bg-indigo-900">
                <FaChartPie className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Templates Grid */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
        >
          <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-gray-100">
            Your Budget Templates
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.length > 0 ? templates.map(template => {
              const templateTotal = calculateTemplateTotal(template.categories);
              const isExpanded = expandedTemplates[template.id] || false;

              return (
                <motion.div
                  key={template.id}
                  whileHover={{ y: -5 }}
                  className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow relative overflow-hidden group"
                >
                  {/* Template Header */}
                  <div 
                    onClick={() => setExpandedTemplates(prev => ({
                      ...prev,
                      [template.id]: !prev[template.id]
                    }))}
                    className="flex justify-between items-start mb-4 cursor-pointer"
                  >
                    <div>
                      <h3 className="font-semibold text-xl text-gray-900 dark:text-gray-100">
                        {template.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {template.categories.length} categories • Total: {
                          Object.entries(
                            template.categories.reduce((acc, cat) => {
                              const currency = cat.currency;
                              const amount = Number(cat.amount) || 0;
                              acc[currency] = (acc[currency] || 0) + amount;
                              return acc;
                            }, {})
                          )
                          .map(([currency, total]) => `${CURRENCY_SYMBOLS_TEXT[currency]}${total.toFixed(2)}`)
                          .join(', ')
                        }
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingTemplate(template);
                          setIsTemplateModalOpen(true);
                        }}
                        className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-indigo-100 dark:hover:bg-indigo-900"
                      >
                        <FaPencilAlt className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setItemToDelete(template);
                          setDeleteModalOpen(true);
                        }}
                        className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-900"
                      >
                        <FaTrash className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </button>
                    </div>
                  </div>

                  {/* Template Description */}
                  {template.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      {template.description}
                    </p>
                  )}

                  {/* Category List - Collapsible */}
                  <motion.div 
                    initial={false}
                    animate={{ height: isExpanded ? 'auto' : 0 }}
                    className="overflow-hidden"
                  >
                    <div className="space-y-3 mb-4">
                      {template.categories.map((cat, index) => (
                        <div 
                          key={index}
                          className="flex justify-between items-center p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50"
                        >
                          <span className="text-gray-700 dark:text-gray-300">
                            {cat.category}
                          </span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {CURRENCY_SYMBOLS[cat.currency]}{Number(cat.amount).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  {/* Apply Button */}
                  <button
                    onClick={() => handleApplyTemplate(template.id, selectedMonth)}
                    disabled={monthlyBudgets[selectedMonth]?.templateId === template.id && 
                              areTemplatesEqual(template.categories, monthlyBudgets[selectedMonth]?.categories)}
                    className={`w-full mt-4 py-2.5 px-4 rounded-lg transition-all transform 
                      ${monthlyBudgets[selectedMonth]?.templateId === template.id && 
                        areTemplatesEqual(template.categories, monthlyBudgets[selectedMonth]?.categories)
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600 hover:scale-[1.02] focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-gray-800'
                      }`}
                  >
                    {monthlyBudgets[selectedMonth]?.templateId === template.id
                      ? areTemplatesEqual(template.categories, monthlyBudgets[selectedMonth]?.categories)
                        ? 'Applied'
                        : 'Template Updated - Click to Apply'
                      : `Apply to ${format(new Date(selectedMonth + '-01'), 'MMMM yyyy')}`}
                  </button>
                </motion.div>
              );
            }) : (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="col-span-full text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow-lg"
              >
                <FaRegLightbulb className="w-12 h-12 text-indigo-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  No Templates Yet
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Create your first budget template to get started!
                </p>
                <button
                  onClick={() => {
                    setEditingTemplate(null);
                    setIsTemplateModalOpen(true);
                  }}
                  className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Create Template
                </button>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Monthly Budget Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden"
        >
          <div className="p-4 md:p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
              <h2 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4 md:mb-0">
                {format(new Date(selectedMonth + '-01'), 'MMMM yyyy')} Budget
              </h2>
            </div>
            
            <div className="p-2 md:p-6">
              <Tab.Group>
                <Tab.List className="flex space-x-4 mb-6 border-b border-gray-200 dark:border-gray-700 overflow-x-auto scrollbar-hide">
                  <Tab className={({ selected }) => `
                    px-3 md:px-4 py-2 text-sm font-medium focus:outline-none whitespace-nowrap
                    ${selected 
                      ? 'text-indigo-600 border-b-2 border-indigo-600' 
                      : 'text-gray-500 hover:text-teal-700'}
                  `}>
                    <div className="flex items-center space-x-2">
                      <FaChartBar className="w-4 h-4" />
                      <span>Overview</span>
                    </div>
                  </Tab>
                  <Tab className={({ selected }) => `
                    px-3 md:px-4 py-2 text-sm font-medium focus:outline-none whitespace-nowrap
                    ${selected 
                      ? 'text-indigo-600 border-b-2 border-indigo-600' 
                      : 'text-gray-500 hover:text-teal-700'}
                  `}>
                    <div className="flex items-center space-x-2">
                      <FaList className="w-4 h-4" />
                      <span>Details</span>
                    </div>
                  </Tab>
                </Tab.List>

                <Tab.Panels>
                  {/* Overview Panel */}
                  <Tab.Panel>
                    {(() => {
                      const { currencyTotals } = calculateMonthlyTotals();
                      return (
                        <div className="grid gap-6 mb-6">
                          {/* Currency Summary Cards */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {Object.entries(currencyTotals).map(([currency, totals]) => (
                              <div 
                                key={currency}
                                className="bg-violet-50 dark:bg-gray-900 rounded-xl p-3 sm:p-4 md:p-6 shadow-sm"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="text-left">
                                    <h3 className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">
                                      Budget ({currency})
                                    </h3>
                                    <p className="text-base sm:text-lg md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                                      {CURRENCY_SYMBOLS[currency]}{totals.budget.toFixed(2)}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <h3 className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">
                                      Spent ({currency})
                                    </h3>
                                    <p className={`text-base sm:text-lg md:text-2xl font-bold ${
                                      totals.spent > totals.budget ? 'text-red-500' : 'text-green-500'
                                    }`}>
                                      {CURRENCY_SYMBOLS[currency]}{totals.spent.toFixed(2)}
                                    </p>
                                  </div>
                                </div>
                                {totals.budget > 0 && (
                                  <div className="mt-3 sm:mt-4">
                                    <div className="relative pt-1">
                                      <div className="overflow-hidden h-2 text-xs flex rounded-full bg-gray-200 dark:bg-gray-700">
                                        <div
                                          style={{ width: `${Math.min((totals.spent / totals.budget) * 100, 100)}%` }}
                                          className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${
                                            totals.spent > totals.budget ? 'bg-red-500' : 'bg-green-500'
                                          }`}
                                        />
                                      </div>
                                      <p className="text-xs text-right text-gray-600 dark:text-gray-400 mt-4">
                                        {((totals.spent / totals.budget) * 100).toFixed(1)}% of budget have been used
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Chart Section with Currency Selector */}
                          <div className="space-y-4">
                            <div className="ml-4 flex justify-start items-center gap-2">
                              <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">View chart in:</span>
                              <select
                                value={selectedCurrency}
                                onChange={(e) => setSelectedCurrency(e.target.value)}
                                className="border border-gray-200 dark:border-gray-700 rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm bg-white dark:bg-gray-800"
                              >
                                {userCurrencies.map(currency => (
                                  <option key={currency} value={currency}>
                                    {currency}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="sm:overflow-hidden overflow-x-auto sm:w-auto w-[calc(100vw-3rem)] -mx-6 sm:mx-0 px-6 sm:px-0 touch-pan-x">
                              <div className="min-w-[600px] max-w-[800px] mx-auto">
                                <BarChartStructure
                                  data={prepareBudgetChartData(selectedCurrency)}
                                  dataKeys={['Budget', 'Spent']}
                                  xAxisKey="name"
                                  title={`${format(new Date(selectedMonth + '-01'), 'MMMM yyyy')} Budget vs. Actual (${selectedCurrency})`}
                                  xAxisLabel="Categories"
                                  yAxisLabel={`Amount (${CURRENCY_SYMBOLS[selectedCurrency]})`}
                                  currencySymbol={CURRENCY_SYMBOLS[selectedCurrency]}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </Tab.Panel>

                  {/* Details Panel */}
                  <Tab.Panel>
                    <div className="space-y-4">
                      {/* Controls */}
                      <div className="flex flex-col sm:flex-row gap-4 mb-6">
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-gray-600 dark:text-gray-400">Currency:</label>
                          <select
                            value={selectedCurrency}
                            onChange={(e) => setSelectedCurrency(e.target.value)}
                            className="border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800"
                          >
                            {userCurrencies.map(currency => (
                              <option key={currency} value={currency}>
                                {currency}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-gray-600 dark:text-gray-400">Sort by:</label>
                          <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800"
                          >
                            <option value="category">Category</option>
                            <option value="budget">Budget Amount</option>
                            <option value="spending">Spent Amount</option>
                            <option value="progress">Progress</option>
                          </select>
                          
                          <button
                            onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                            className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            {sortDirection === 'asc' ? '↑' : '↓'}
                          </button>
                        </div>
                      </div>

                      {/* Grid container for category cards */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {categories
                          .map(category => {
                            const budgetCategory = monthlyBudgets[selectedMonth]?.categories?.find(
                              cat => cat.category === category && cat.currency === selectedCurrency
                            );
                            const expenses = monthlyExpenses[category];
                            const progress = getBudgetProgress(category, budgetCategory?.amount || 0, selectedCurrency);
                            
                            // Only show items matching the selected currency
                            if (
                              (!budgetCategory || budgetCategory.currency !== selectedCurrency) && 
                              (!expenses || expenses.currency !== selectedCurrency)
                            ) return null;

                            return {
                              category,
                              budgetCategory,
                              expenses,
                              progress,
                              sortValues: {
                                category: category.toLowerCase(),
                                budget: budgetCategory?.amount || 0,
                                spending: progress.spent,
                                progress: progress.progress
                              }
                            };
                          })
                          .filter(Boolean) // Remove null items
                          .sort((a, b) => {
                            const aValue = a.sortValues[sortBy];
                            const bValue = b.sortValues[sortBy];
                            const modifier = sortDirection === 'asc' ? 1 : -1;
                            
                            if (typeof aValue === 'string') {
                              return aValue.localeCompare(bValue) * modifier;
                            }
                            return (aValue - bValue) * modifier;
                          })
                          .map(({ category, budgetCategory, expenses, progress }) => (
                            <div 
                              key={category}
                              className="bg-white dark:bg-gray-900 rounded-lg p-3 sm:p-4 shadow-sm"
                            >
                              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4 mb-2">
                                <div>
                                  <h3 className="font-medium text-gray-900 dark:text-gray-100 text-sm sm:text-base">
                                    {category}
                                  </h3>
                                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                    Budget: {CURRENCY_SYMBOLS[selectedCurrency]}{budgetCategory?.amount || '0.00'}
                                  </p>
                                </div>
                                <div className="text-right w-full sm:w-auto">
                                  <p className={`font-medium text-xs sm:text-sm ${
                                    !budgetCategory?.amount || progress.isOverBudget ? 'text-red-500' : 'text-green-500'
                                  }`}>
                                    Spent: {CURRENCY_SYMBOLS[selectedCurrency]}{progress.spent.toFixed(2)}
                                  </p>
                                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                    Remaining: {CURRENCY_SYMBOLS[selectedCurrency]}{progress.remaining.toFixed(2)}
                                  </p>
                                </div>
                              </div>
                              <div className="relative pt-1">
                                <div className="overflow-hidden h-2 text-xs flex rounded-full bg-gray-200 dark:bg-gray-700">
                                  <div
                                    style={{ width: `${Math.min(progress.progress, 100)}%` }}
                                    className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${
                                      !budgetCategory?.amount || progress.isOverBudget ? 'bg-red-500' : 'bg-green-500'
                                    }`}
                                  />
                                </div>
                                <p className="text-xs text-right mt-1 text-gray-600 dark:text-gray-400">
                                  {!budgetCategory?.amount ? 'No budget set' : `${progress.progress.toFixed(1)}% of budget`}
                                </p>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  </Tab.Panel>
                </Tab.Panels>
              </Tab.Group>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Modals */}
      {isTemplateModalOpen && (
        <BudgetTemplateModal
          onClose={() => {
            setIsTemplateModalOpen(false);
            setEditingTemplate(null);
          }}
          onSubmit={editingTemplate ? handleUpdateTemplate : handleCreateTemplate}
          editingTemplate={editingTemplate}
          categories={categories}
          updateCategories={setCategories}
          currencies={userCurrencies}
        />
      )}

      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={() => handleDeleteTemplate(itemToDelete.id)}
        itemName={itemToDelete?.title}
        itemType="budget template"
      />
    </div>
  );
};

export default Budgets;
